import { SEATS_3P, SEATS_4P } from './types.js';
import type { ResultInput, Rule, Seat } from './types.js';
import { isBankrupt } from './calculate.js';

/** 素点の許容範囲（§4.4.2） */
export const MIN_RAW_SCORE = -100_000;
export const MAX_RAW_SCORE = 300_000;

/** エラーコード。§8.2 のAPI エラー一覧と対応する */
export type ValidationErrorCode =
  | 'PLAYER_COUNT_MISMATCH'
  | 'SCORE_SUM_MISMATCH'
  | 'SCORE_OUT_OF_RANGE'
  | 'DUPLICATE_MEMBER'
  | 'INVALID_SEAT'
  | 'SEAT_REQUIRED_FOR_TIE'
  | 'BUSTER_REQUIRED'
  | 'INVALID_BUSTER';

export interface ValidationError {
  code: ValidationErrorCode;
  message: string;
  detail?: Record<string, unknown>;
}

export type ValidationWarningCode = 'SCORE_NOT_MULTIPLE_OF_100';

export interface ValidationWarning {
  code: ValidationWarningCode;
  message: string;
  detail?: Record<string, unknown>;
}

export interface ValidationResult {
  ok: boolean;
  errors: ValidationError[];
  /** 保存を妨げないが利用者に知らせるべき事項 */
  warnings: ValidationWarning[];
}

const jp = (n: number): string => n.toLocaleString('ja-JP');

/**
 * 素点が同じ参加者のグループを返す（§4.4.3）。
 * 2人以上のグループのみ返すため、戻り値が空なら同点は発生していない。
 */
export function findTiedGroups(inputs: ResultInput[]): ResultInput[][] {
  const byScore = new Map<number, ResultInput[]>();
  for (const r of inputs) {
    const group = byScore.get(r.rawScore);
    if (group) {
      group.push(r);
    } else {
      byScore.set(r.rawScore, [r]);
    }
  }
  return [...byScore.values()].filter((g) => g.length >= 2);
}

/**
 * 席の入力が必須になっている memberId を返す（§4.4.3）。
 *
 * UI はこの結果を見て、該当する行にだけ席セレクタを展開する。素点が編集されて
 * 同点が解消されれば、次の呼び出しで対象から外れる。
 */
export function membersRequiringSeat(inputs: ResultInput[]): string[] {
  return findTiedGroups(inputs).flatMap((group) => group.map((r) => r.memberId));
}

/**
 * 飛ばした人の指定が必要な memberId を返す（§4.4.1）。
 *
 * 「該当なし」を選んだ場合は bustedBy に null を明示的に入れる。プロパティ自体が
 * 無い（undefined）状態が「未選択」であり、保存を止める対象になる。
 */
export function membersRequiringBuster(inputs: ResultInput[], rule: Rule): string[] {
  if (!rule.bankruptcy.enabled || rule.bankruptcy.payTo !== 'buster') {
    return [];
  }
  return inputs
    .filter((r) => isBankrupt(r.rawScore, rule) && r.bustedBy === undefined)
    .map((r) => r.memberId);
}

/**
 * 対局の入力を検証する（仕様書 §4.4.2）。
 *
 * クライアントとサーバーの両方から同じ関数を呼ぶ。サーバーはクライアントの検証を
 * 信頼せず、保存前に必ず再実行すること（§4.4.3 サーバー挙動）。
 */
export function validateMatch(inputs: ResultInput[], rule: Rule): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // --- 人数 ----------------------------------------------------------------
  if (inputs.length !== rule.playerCount) {
    errors.push({
      code: 'PLAYER_COUNT_MISMATCH',
      message: `${rule.playerCount}人分の入力が必要です（現在: ${inputs.length}人）`,
      detail: { expected: rule.playerCount, actual: inputs.length },
    });
    // 人数が違う時点で合計や席の検証は意味を持たないため、ここで打ち切る
    return { ok: false, errors, warnings };
  }

  // --- メンバーの重複 ------------------------------------------------------
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const r of inputs) {
    if (seen.has(r.memberId)) duplicated.add(r.memberId);
    seen.add(r.memberId);
  }
  if (duplicated.size > 0) {
    errors.push({
      code: 'DUPLICATE_MEMBER',
      message: '同じメンバーが重複して選択されています',
      detail: { memberIds: [...duplicated] },
    });
  }

  // --- 素点の範囲と単位 ----------------------------------------------------
  const outOfRange = inputs.filter(
    (r) => !Number.isFinite(r.rawScore) || r.rawScore < MIN_RAW_SCORE || r.rawScore > MAX_RAW_SCORE,
  );
  if (outOfRange.length > 0) {
    errors.push({
      code: 'SCORE_OUT_OF_RANGE',
      message: `素点は ${jp(MIN_RAW_SCORE)} 〜 ${jp(MAX_RAW_SCORE)} の範囲で入力してください`,
      detail: { memberIds: outOfRange.map((r) => r.memberId) },
    });
  }

  const notRounded = inputs.filter((r) => Number.isFinite(r.rawScore) && r.rawScore % 100 !== 0);
  if (notRounded.length > 0) {
    warnings.push({
      code: 'SCORE_NOT_MULTIPLE_OF_100',
      message: '素点が100点単位になっていません',
      detail: { memberIds: notRounded.map((r) => r.memberId) },
    });
  }

  // --- 素点の合計 ----------------------------------------------------------
  const expectedSum = rule.startPoints * rule.playerCount;
  const actualSum = inputs.reduce((s, r) => s + (Number.isFinite(r.rawScore) ? r.rawScore : 0), 0);
  if (actualSum !== expectedSum) {
    const diff = expectedSum - actualSum;
    errors.push({
      code: 'SCORE_SUM_MISMATCH',
      message:
        diff > 0
          ? `合計が${jp(diff)}点足りません（${jp(expectedSum)}点になるように入力してください）`
          : `合計が${jp(-diff)}点多すぎます（${jp(expectedSum)}点になるように入力してください）`,
      detail: { expected: expectedSum, actual: actualSum },
    });
  }

  // --- 席 ------------------------------------------------------------------
  const validSeats: readonly Seat[] = rule.playerCount === 3 ? SEATS_3P : SEATS_4P;
  const seatUsage = new Map<Seat, string[]>();

  for (const r of inputs) {
    if (!r.seat) continue;
    if (!validSeats.includes(r.seat)) {
      errors.push({
        code: 'INVALID_SEAT',
        message: `${rule.playerCount}人打ちでは「${seatLabel(r.seat)}」は使用できません`,
        detail: { memberId: r.memberId, seat: r.seat },
      });
      continue;
    }
    const users = seatUsage.get(r.seat);
    if (users) {
      users.push(r.memberId);
    } else {
      seatUsage.set(r.seat, [r.memberId]);
    }
  }

  for (const [seat, memberIds] of seatUsage) {
    if (memberIds.length > 1) {
      errors.push({
        code: 'INVALID_SEAT',
        message: `「${seatLabel(seat)}」が重複しています`,
        detail: { seat, memberIds },
      });
    }
  }

  // --- 同点時の席（§4.4.3） -------------------------------------------------
  // 席は本来この計算に不要な情報なので、同点で順位の根拠が必要になったときだけ求める。
  const missingSeat = findTiedGroups(inputs)
    .flatMap((group) => group)
    .filter((r) => !r.seat)
    .map((r) => r.memberId);

  if (missingSeat.length > 0) {
    errors.push({
      code: 'SEAT_REQUIRED_FOR_TIE',
      message: '同点の参加者がいます。順位を決めるため、その全員の席を入力してください',
      detail: { memberIds: missingSeat },
    });
  }

  // --- 飛ばした人（§4.4.1 / §4.4.2） ---------------------------------------
  if (rule.bankruptcy.enabled && rule.bankruptcy.payTo === 'buster') {
    const participantIds = new Set(inputs.map((r) => r.memberId));

    const unspecified = membersRequiringBuster(inputs, rule);
    if (unspecified.length > 0) {
      errors.push({
        code: 'BUSTER_REQUIRED',
        message: '飛んだ人ごとに、飛ばした人または「該当なし」を選んでください',
        detail: { memberIds: unspecified },
      });
    }

    for (const r of inputs) {
      if (!r.bustedBy) continue;
      if (r.bustedBy === r.memberId) {
        errors.push({
          code: 'INVALID_BUSTER',
          message: '自分自身を飛ばした人として指定することはできません',
          detail: { memberId: r.memberId },
        });
      } else if (!participantIds.has(r.bustedBy)) {
        errors.push({
          code: 'INVALID_BUSTER',
          message: '飛ばした人はこの対局の参加者から選んでください',
          detail: { memberId: r.memberId, bustedBy: r.bustedBy },
        });
      }
    }
  }

  return { ok: errors.length === 0, errors, warnings };
}

/** 席の日本語表記 */
export function seatLabel(seat: Seat): string {
  return { E: '東', S: '南', W: '西', N: '北' }[seat];
}
